import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// dev/app-skeleton/src/pages/Signup.tsx
// D14-DEV-v2 / SEC C' 修：删占位 → 真注册流程。
//   email + password + password 确认
//   → client 生成 user_id (UUID) + kdf_salt (16B) + root_key (32B)
//   → deriveKEK(password, kdf_salt) = KEK (Argon2id)
//   → wrapRootKey(KEK, root_key, user_id) = 72B [nonce|ct|tag]，AAD = "rkw|v1|" + user_id
//   → zeroize(KEK)（wrapRootKey 内部已清；这里 root_key 转交给 setRootKey）
//   → POST /api/auth/register { user_id, email, login_password, kdf_salt_b64, root_key_wrapped_b64, crypto_version:1 }
//   → 服务端写 D1 users 行（password_hash = PBKDF2(login_password, server_salt, 100k)）+ 起 session
//   → setCurrentUserId + setRootKey + nav('/today')
// SEC 要点：login_password 即 passphrase（D14 决策：服务端登录密码 = 主密码；两个 KDF（client Argon2id / server PBKDF2）
//          分别派出 KEK 与 password_hash，互不重用）。
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { setRootKey } from "../lib/session-key";
import { setCurrentUserId } from "../lib/current-user";
import { CRYPTO_VERSION, bytesToBase64, deriveKEK, newKdfSalt, newRootKey, wrapRootKey, zeroize, } from "../crypto";
export function SignupPage() {
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    async function onSubmit(e) {
        e.preventDefault();
        setErr("");
        const emailTrim = email.trim().toLowerCase();
        if (!emailTrim || !/.+@.+\..+/.test(emailTrim)) {
            setErr("请输入有效邮箱");
            return;
        }
        if (pw.length < 8) {
            setErr("密码至少 8 位");
            return;
        }
        if (pw !== pw2) {
            setErr("两次输入的密码不一致");
            return;
        }
        setBusy(true);
        try {
            const userId = crypto.randomUUID();
            const kdfSalt = newKdfSalt(); // 16B
            const rootKey = newRootKey(); // 32B
            const kek = await deriveKEK(pw, kdfSalt); // Argon2id
            // wrapRootKey 内部 zeroize(kek)
            let wrappedB64;
            try {
                const { wrapped } = wrapRootKey(kek, rootKey, userId);
                wrappedB64 = bytesToBase64(wrapped);
            }
            catch (e) {
                zeroize(rootKey);
                throw e;
            }
            const r = await fetch("/api/auth/register", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: userId,
                    email: emailTrim,
                    login_password: pw,
                    kdf_salt_b64: bytesToBase64(kdfSalt),
                    root_key_wrapped_b64: wrappedB64,
                    crypto_version: CRYPTO_VERSION,
                }),
            });
            if (r.status === 429) {
                zeroize(rootKey);
                setErr("请求过于频繁，请稍后再试");
                return;
            }
            if (r.status === 409) {
                zeroize(rootKey);
                setErr("该邮箱已注册，请前往解锁");
                return;
            }
            if (!r.ok) {
                zeroize(rootKey);
                setErr("注册失败，请稍后再试");
                return;
            }
            // 注册成功 → 直接进 today（session 已由 worker createSession 起好）
            await setCurrentUserId(userId);
            setRootKey(rootKey);
            nav("/today", { replace: true });
        }
        catch (e) {
            console.error("[signup] error:", e);
            setErr("网络错误，请检查连接后重试");
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("main", { className: "min-h-screen flex flex-col items-center justify-center px-md", "data-testid": "page-signup", children: [_jsx("img", { src: "/icons/icon-192.png", alt: "", width: 96, height: 96, style: { imageRendering: "pixelated" } }), _jsx("h1", { className: "text-2xl font-medium mt-md", children: "Nocty" }), _jsx("p", { className: "text-text-muted text-sm mt-sm text-center max-w-xs", children: "\u4F60\u7684\u79C1\u4EBA\u52A0\u5BC6\u65E5\u8BB0 \u00B7 \u7AEF\u5230\u7AEF\u52A0\u5BC6\uFF0C\u6211\u4EEC\u770B\u4E0D\u5230\u5185\u5BB9\u3002" }), _jsxs("form", { onSubmit: onSubmit, className: "flex flex-col gap-sm mt-xl w-full max-w-xs", children: [_jsx("input", { type: "email", placeholder: "\u90AE\u7BB1", value: email, onChange: (e) => { setEmail(e.target.value); setErr(""); }, className: "card px-md py-sm bg-surface text-text", style: { minHeight: 44 }, autoComplete: "email", "aria-label": "\u90AE\u7BB1", "data-testid": "input-email", required: true }), _jsx("input", { type: "password", placeholder: "\u4E3B\u5BC6\u7801\uFF08\u2265 8 \u4F4D\uFF0C\u6211\u4EEC\u65E0\u6CD5\u627E\u56DE\uFF09", value: pw, onChange: (e) => { setPw(e.target.value); setErr(""); }, className: "card px-md py-sm bg-surface text-text", style: { minHeight: 44 }, autoComplete: "new-password", "aria-label": "\u4E3B\u5BC6\u7801", "data-testid": "input-password", minLength: 8, required: true }), _jsx("input", { type: "password", placeholder: "\u518D\u6B21\u8F93\u5165\u4E3B\u5BC6\u7801", value: pw2, onChange: (e) => { setPw2(e.target.value); setErr(""); }, className: "card px-md py-sm bg-surface text-text", style: { minHeight: 44 }, autoComplete: "new-password", "aria-label": "\u518D\u6B21\u8F93\u5165\u4E3B\u5BC6\u7801", "data-testid": "input-password-confirm", minLength: 8, required: true }), err && _jsx("div", { className: "text-danger text-sm", role: "alert", "data-testid": "signup-err", children: err }), _jsx("button", { type: "submit", className: "btn btn-primary", style: { minHeight: 44 }, disabled: busy, "data-testid": "btn-signup", children: busy ? "创建中…" : "创建账户" }), _jsx(Link, { to: "/unlock", className: "btn btn-ghost text-center", style: { minHeight: 44 }, "data-testid": "link-unlock", children: "\u5DF2\u6709\u8D26\u6237 \u00B7 \u89E3\u9501" })] }), _jsx("p", { className: "text-text-muted text-xs mt-xl text-center max-w-xs", children: "\u4E3B\u5BC6\u7801\u5728\u672C\u673A\u6D3E\u751F\u5BC6\u94A5 \u00B7 \u6C38\u4E0D\u4E0A\u4F20\u670D\u52A1\u7AEF\u3002\u8BF7\u59A5\u5584\u4FDD\u7BA1 \u2014\u2014 \u5FD8\u8BB0 = \u6570\u636E\u65E0\u6CD5\u6062\u590D\u3002" })] }));
}
